import { useCallback, useEffect, useMemo, useState } from 'react';
/** COMPONENTS */
import Recurrence from './Recurrence';
import InputError from '../InputError';
import * as SchedulerUI from './SchedulerUI';
/** UTILS */
import { RRule, rrulestr } from 'rrule';
import {
  SCHEDULE_DESCRIPTION_MAX_LENGTH,
  SCHEDULE_NAME_MAX_LENGTH,
} from '../../config/constants';
import { alertThresholdToInput, buildScheduleLimitMessage } from './utils';
import {
  AlertThresholdKind,
  IAlertThreshold,
  IAlertThresholdAnomaly,
  IScheduleFieldsFragment,
  IScheduleHistoryFieldsFragment,
  IScheduleInput,
  ISlackDestinationFieldsFragment,
  QueryType,
  ScheduleType,
} from '../../utils/apollo/graphqlSchema';
import { isArrayLike, isNil } from 'lodash';
import { assertNotNil } from '../../utils/types';
import KubitModal from '../KubitModal';
import { ModalActions } from '../KubitModal/KubitModalUI';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { closeModal } from '../../redux/slices/modal';
import { PrimaryButton, SecondaryButton } from '../Button';
import moment from 'moment';
import {
  DescriptionField,
  EmailInputField,
  FieldLabel,
  NameField,
  VerticalField,
} from '../Form';
import AlertThresholds from './AlertThresholds';
import { analysisSelector } from '../../redux/selectors/analyses';
import SchedulerAlertOnly from './SchedulerAlertOnly';
import SwitchiOS from '../SwitchiOS';
import KubitTooltip from '../KubitTooltip';
import { brazeRemoveOutdatedTooltipOption } from '../KubitTooltip/globalTooltips';
import useSchedulerApi from './useSchedulerApi';
import SEARCH_SCHEDULES from '../../pages/Schedule/apollo/SEARCH_SCHEDULES';
import { useApolloClient } from '@apollo/client';
import { MaybeNil } from '../../utils/interfaces';
import { toSlackDestinationInput } from '../Slack/utils';
import { SlackDestinationRow } from './SlackDestinationRow';
import { UnreachableCaseError } from 'ts-essentials';
import ReportAlertSelectMenu from './ReportAlertSelectMenu';
import { IReportItem } from './ReportAlertSelectMenu/ReportAlertSelectMenu';
import useScheduleReportForSelection from './ReportAlertSelectMenu/useScheduleReportForSelection';
import EmailTagList from '../EmailTagList';

type BrazeImportType = 'one-time' | 'scheduled';

interface ISchedulerErrors {
  name: boolean;
  recipients: string | null;
  recurrence: boolean;
}

interface IProps {
  entityId?: string;
  jobType: ScheduleType;
  recurringOnly?: boolean;
  prefilledName?: string;
  isStaticDateRangeCohort?: boolean;
  // XXX when creating a new Braze Cohort Import, the modal data redux state
  // is actually used for the cohort data and is never a "schedule edit" entity
  currentSchedule?:
    | (IScheduleFieldsFragment & IScheduleHistoryFieldsFragment)
    | null;
}

// XXX a function to force a new ref every time
const emptySchedulerErrors = (): ISchedulerErrors => {
  return {
    name: false,
    recipients: null,
    recurrence: false,
  };
};

const hasErrors = (errors: ISchedulerErrors) =>
  errors.name || errors.recipients || errors.recurrence;

export default function SchedulerLoadedForm({
  entityId,
  jobType,
  prefilledName,
  recurringOnly = false,
  isStaticDateRangeCohort,
  currentSchedule,
}: IProps) {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  const { currentReportSelection, allReports } =
    useScheduleReportForSelection(currentSchedule);

  /* Global form errors */
  const [errors, setErrors] = useState<ISchedulerErrors>(
    emptySchedulerErrors(),
  );

  /* Schedule name */
  const [scheduleName, setScheduleName] = useState(
    currentSchedule?.name || prefilledName || '',
  );
  const onSchedulerNameChange = useCallback(
    (value: string) => {
      setScheduleName(value);
      setErrors({
        ...errors,
        name: false,
      });
    },
    [errors],
  );

  /* Schedule description */
  const [scheduleDescription, setScheduleDescription] = useState(
    currentSchedule?.description || '',
  );

  const [slackDestination, setSlackDestination] = useState<
    MaybeNil<ISlackDestinationFieldsFragment>
  >(currentSchedule?.slackDestination);

  const brazeImportNotRecurring =
    jobType === ScheduleType.BrazeCohortImport && recurringOnly === false;

  const [importType, setImportType] = useState<BrazeImportType>(
    brazeImportNotRecurring ? 'one-time' : 'scheduled',
  );

  const handleChangeImportType = (newImportType: string) => {
    setImportType(newImportType as BrazeImportType);
  };

  const [brazeRemoveOutdated, setBrazeRemoveOutdated] = useState<boolean>(
    !!(
      currentSchedule?.entity &&
      'brazeRemoveOutdated' in currentSchedule?.entity &&
      currentSchedule.entity.brazeRemoveOutdated
    ),
  );
  const handleBrazeRemoveOutdatedClick = () => {
    if (currentSchedule) {
      return; // not subject to edit
    }
    setBrazeRemoveOutdated(!brazeRemoveOutdated);
  };

  const exportOptions = useMemo(
    () => [
      {
        value: 'one-time',
        label: 'One-Time Export',
      },
      {
        value: 'scheduled',
        label: 'Scheduled Export',
        disabled: isStaticDateRangeCohort,
      },
    ],
    [isStaticDateRangeCohort],
  );

  const getEntityId = useCallback(() => {
    if (currentSchedule) {
      switch (currentSchedule.entity.__typename) {
        case 'DashboardScheduleEntity':
          return currentSchedule.entity.item.baseId;
        case 'AnalysisScheduleEntity':
        case 'CohortScheduleEntity':
          return currentSchedule.entity.item.id;
        default:
          throw new UnreachableCaseError(currentSchedule.entity);
      }
    } else {
      assertNotNil(entityId);
      return entityId;
    }
  }, [currentSchedule, entityId]);

  // Store a list of emails
  const [recipients, setRecipients] = useState<string[]>(
    currentSchedule?.emailList ?? [],
  );
  const [selectedReports, setSelectedReports] = useState<IReportItem[]>(
    currentReportSelection,
  );

  /* alerts */
  const [isAlertOnly, setIsAlertOnly] = useState<boolean>(
    currentSchedule?.alertOnly ?? false,
  );

  const defaultThresholds =
    jobType !== ScheduleType.BrazeCohortImport
      ? [
          {
            kind: AlertThresholdKind.Anomaly,
            __typename: 'AlertThresholdAnomaly',
          } satisfies IAlertThresholdAnomaly,
        ]
      : [];

  const [alertThresholds, setAlertThresholds] = useState<
    (IAlertThreshold | null)[]
  >(
    currentSchedule?.entity?.__typename === 'AnalysisScheduleEntity'
      ? currentSchedule.entity.alertThresholds.list
      : defaultThresholds,
  );

  const setIsAlertOnlyAndResetThresholds = (newAlertOnly: boolean) => {
    if (!newAlertOnly) {
      setAlertThresholds(defaultThresholds);
    }
    setIsAlertOnly(newAlertOnly);
  };

  const currentScheduleAnalysis =
    currentSchedule?.entity?.__typename === 'AnalysisScheduleEntity'
      ? currentSchedule.entity.item
      : null;

  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentStoreAnalysis = useAppSelector((state) => {
    if (jobType !== ScheduleType.Analysis) {
      return null;
    }
    return entityId ? currentAnalysisMemoizedSelector(state, entityId) : null;
  });

  const allowCustomThresholds = currentScheduleAnalysis
    ? currentScheduleAnalysis?.queryType === QueryType.Expression
    : currentStoreAnalysis?.metadata?.queryType === QueryType.Expression;

  let comparisonConfig = null;
  if (
    currentScheduleAnalysis &&
    'comparisonConfig' in currentScheduleAnalysis
  ) {
    comparisonConfig = currentScheduleAnalysis.comparisonConfig ?? null;
  } else if (currentStoreAnalysis) {
    comparisonConfig =
      currentStoreAnalysis.metadata?.comparisonInfo?.comparisonConfig ?? null;
  }

  /* RRULE */
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rruleTimezone = currentSchedule?.timezone ?? browserTimeZone;
  const existingRule = currentSchedule?.cron && rrulestr(currentSchedule.cron);
  const [rrule, setRrule] = useState<RRule>(
    existingRule ||
      new RRule({
        freq: RRule.DAILY,
        interval: 1,
        byhour: 8,
        byminute: 0,
        bysecond: 0,
      }),
  );

  const clearRecurrenceError = useCallback(() => {
    setErrors({
      ...errors,
      recurrence: false,
    });
  }, [errors, setErrors]);

  const {
    isScheduleConfigLoading,
    allScheduleNames,
    allThresholdCompareOps,
    newScheduleLimit,
    createSchedule,
    isCreateMutationLoading,
    updateSchedule,
    isUpdateScheduleLoading,
  } = useSchedulerApi({
    jobType,
    currentScheduleName: currentSchedule?.name ?? null,
  });

  useEffect(() => {
    if (currentSchedule && importType === 'one-time') {
      setImportType('scheduled');
    }
  }, [currentSchedule, importType]);

  /* Dynamic name validators */
  const isScheduleNameLengthValid = useMemo(
    () => scheduleName.trim().length <= SCHEDULE_NAME_MAX_LENGTH,
    [scheduleName],
  );

  const isScheduleNameUnique = useMemo(() => {
    return !allScheduleNames.find(
      (n) =>
        scheduleName.toLowerCase() !== currentSchedule?.name?.toLowerCase() &&
        n.toLowerCase() === scheduleName.toLowerCase(),
    );
  }, [scheduleName, allScheduleNames, currentSchedule]);

  /* Dynamic description validators */
  const isScheduleDescriptionLengthValid = useMemo(
    () => scheduleDescription.trim().length <= SCHEDULE_DESCRIPTION_MAX_LENGTH,
    [scheduleDescription],
  );

  /* RRule dynamic validators */
  const isRecurrenceValid = useMemo(
    () =>
      rrule.origOptions.freq === 2 && isArrayLike(rrule.origOptions?.byweekday)
        ? rrule.origOptions?.byweekday.length > 0
        : true,
    [rrule],
  );

  const isNotificationDestinationValid =
    recipients.length > 0 ||
    !isNil(slackDestination) ||
    jobType === ScheduleType.BrazeCohortImport;

  const alertOnlyRequiresAlertReports = useMemo(() => {
    if (!currentSchedule) {
      return false;
    }
    switch (currentSchedule.entity.__typename) {
      case 'DashboardScheduleEntity':
        return true;
      case 'AnalysisScheduleEntity':
      case 'CohortScheduleEntity':
        return false;
      default:
        throw new UnreachableCaseError(currentSchedule.entity);
    }
  }, [currentSchedule]);

  const alertReportIsValid =
    !isAlertOnly ||
    (isAlertOnly &&
      (!alertOnlyRequiresAlertReports || selectedReports.length > 0));

  const isConfirmButtonEnabled =
    !!scheduleName &&
    scheduleName.trim().length !== 0 &&
    isScheduleNameLengthValid &&
    isScheduleNameUnique &&
    (!scheduleDescription || isScheduleDescriptionLengthValid) &&
    isNotificationDestinationValid &&
    isRecurrenceValid &&
    alertReportIsValid;

  const newScheduleLimitExceeded =
    newScheduleLimit.exceeded && importType === 'scheduled';

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const selectedAnalysesReports: string[] = useMemo(
    () =>
      isAlertOnly && selectedReports.length === allReports.length
        ? []
        : selectedReports.map((report) => report.value),
    [isAlertOnly, selectedReports, allReports],
  );

  /* Execute create schedule mutation */
  const onConfirm = useCallback(async () => {
    const currentErrors = emptySchedulerErrors();
    if (
      !scheduleName ||
      !scheduleName.trim() ||
      !isScheduleNameLengthValid ||
      !isScheduleNameUnique
    ) {
      currentErrors.name = true;
    }
    if (
      rrule?.origOptions?.freq === 2 &&
      isArrayLike(rrule.origOptions?.byweekday) &&
      rrule.origOptions?.byweekday?.length === 0
    ) {
      currentErrors.recurrence = true;
    }

    if (hasErrors(currentErrors)) {
      setErrors(currentErrors);
      return;
    }

    /* Convert recipients to coma separated emails */
    const scheduleInput: IScheduleInput = {
      name: scheduleName.trim(),
      description: scheduleDescription,
      entityId: getEntityId(),
      type: currentSchedule?.type || jobType,
      cron: rrule.toString().substring(6),
      paused: false, // XXX updating a Schedule always un-pauses it
      timezone: rruleTimezone,
      emailList: [],
      alertOnly: null, // will be set later
      alertAnalysisIds: selectedAnalysesReports,
      alertThresholds:
        jobType === ScheduleType.Analysis
          ? {
              // flatMap == filterNotNull
              list: alertThresholds
                .flatMap((x) => (x != null ? [x] : []))
                .map(alertThresholdToInput),
            }
          : null,
      brazeRemoveOutdated:
        jobType === ScheduleType.BrazeCohortImport ? brazeRemoveOutdated : null,
      slackDestination: toSlackDestinationInput(slackDestination),
    };

    if (jobType !== ScheduleType.BrazeCohortImport) {
      scheduleInput.emailList = recipients;
      scheduleInput.alertOnly = isAlertOnly;
    }

    if (
      importType === 'one-time' &&
      jobType === ScheduleType.BrazeCohortImport &&
      !currentSchedule
    ) {
      scheduleInput.cron = null;
    }

    if (!isScheduleDescriptionLengthValid || !isConfirmButtonEnabled) {
      return;
    }

    if (currentSchedule?.id) {
      await updateSchedule({
        variables: {
          input: scheduleInput,
          id: currentSchedule.id,
        },
      });
    } else {
      await createSchedule({
        variables: {
          input: scheduleInput,
        },
      });
    }

    // refetch all cohort schedules to keep dictionary up to date with the new schedule
    client.refetchQueries({
      include: [SEARCH_SCHEDULES],
    });

    handleClose();
  }, [
    scheduleName,
    isScheduleNameLengthValid,
    isScheduleNameUnique,
    rrule,
    scheduleDescription,
    getEntityId,
    currentSchedule,
    jobType,
    rruleTimezone,
    selectedAnalysesReports,
    alertThresholds,
    brazeRemoveOutdated,
    slackDestination,
    importType,
    isScheduleDescriptionLengthValid,
    isConfirmButtonEnabled,
    client,
    handleClose,
    recipients,
    isAlertOnly,
    updateSchedule,
    createSchedule,
  ]);

  const modalHeader = currentSchedule
    ? 'Edit Schedule'
    : jobType === ScheduleType.BrazeCohortImport
      ? 'Export to Braze'
      : 'Create New Schedule';

  const shortTimeZone = moment().tz(rruleTimezone).zoneAbbr();
  const showReportAlertSelectMenu = jobType === ScheduleType.Dashboard;

  const handleAddRecipient = (email: string, emailError?: string) => {
    if (!email.length && !emailError) {
      if (errors.recipients) {
        setErrors((prevState) => ({
          ...prevState,
          recipients: null,
        }));
      }
      return;
    }

    if (email.length > 0) {
      setRecipients((prevState) => [...prevState, email]);

      if (errors.recipients) {
        setErrors((prevState) => ({
          ...prevState,
          recipients: null,
        }));
      }
    } else if (emailError && emailError !== errors.recipients) {
      setErrors((prevState) => ({
        ...prevState,
        recipients: emailError,
      }));
    }
  };
  const handlePasteRecipients = (emails: string[]) => {
    setRecipients((prevState) => [...prevState, ...emails]);
    setErrors((prevState) => ({
      ...prevState,
      recipients: null,
    }));
  };
  const handleRemoveRecipient = (recipientForDelete: string) => {
    setRecipients((prevState) =>
      prevState.filter((recipient) => recipient !== recipientForDelete),
    );
  };

  return (
    <KubitModal header={modalHeader} onClose={handleClose} open>
      {newScheduleLimitExceeded && (
        <SchedulerUI.Row>
          <InputError
            error={
              `You have reached the maximum limit of ${buildScheduleLimitMessage(
                newScheduleLimit,
              )} schedules. ` +
              'Please go to Schedule on the sidebar menu and manage existing ' +
              'schedules'
            }
          />
        </SchedulerUI.Row>
      )}
      <NameField
        label={'Schedule name'}
        value={scheduleName}
        onChange={onSchedulerNameChange}
        targetErrorElement={'Schedule'}
        maxLength={SCHEDULE_NAME_MAX_LENGTH}
        isDuplicate={!isScheduleNameUnique}
      />
      <DescriptionField
        label={'Schedule description'}
        value={scheduleDescription}
        onChange={setScheduleDescription}
        targetErrorElement={'Schedule'}
      />
      {brazeImportNotRecurring && !currentSchedule && (
        <SchedulerUI.ImportOptionsRadioGroup
          name="braze-cohort-import-type"
          value={importType}
          onChange={handleChangeImportType}
          options={exportOptions}
        />
      )}
      {importType === 'scheduled' && (
        <>
          {jobType === ScheduleType.BrazeCohortImport && (
            <SchedulerUI.Row>
              <KubitTooltip
                noOpenDelay={true}
                {...brazeRemoveOutdatedTooltipOption}
              >
                <SchedulerUI.HorizontalField isEnabled={!currentSchedule}>
                  <FieldLabel>Remove Outdated</FieldLabel>
                  <SwitchiOS
                    isChecked={brazeRemoveOutdated}
                    onClick={handleBrazeRemoveOutdatedClick}
                  />
                </SchedulerUI.HorizontalField>
              </KubitTooltip>
            </SchedulerUI.Row>
          )}
          <SchedulerUI.Row>
            <SchedulerUI.StyledLabel>
              {jobType === ScheduleType.BrazeCohortImport
                ? 'Import Recurrence'
                : 'How often would you like the schedule to run?'}
              <SchedulerUI.StyledBadge color="grey" size="primary">
                {shortTimeZone}
              </SchedulerUI.StyledBadge>
            </SchedulerUI.StyledLabel>
            <Recurrence
              rrule={rrule}
              setRule={setRrule}
              isError={errors?.recurrence || false}
              clearError={clearRecurrenceError}
            />
          </SchedulerUI.Row>
        </>
      )}
      {jobType !== ScheduleType.BrazeCohortImport && (
        <>
          <SchedulerUI.StyledLabel>Notifications</SchedulerUI.StyledLabel>
          <VerticalField>
            <EmailInputField
              label={'Which email account to send notifications to?'}
              emails={recipients}
              onChange={handleAddRecipient}
              onPaste={handlePasteRecipients}
              error={errors.recipients}
            />
            {recipients.length > 0 && (
              <EmailTagList
                emails={recipients}
                onRemove={handleRemoveRecipient}
              />
            )}
          </VerticalField>
          <SlackDestinationRow
            value={slackDestination}
            onChange={setSlackDestination}
          />
          <SchedulerAlertOnly
            alertOnly={isAlertOnly}
            setAlertOnly={setIsAlertOnlyAndResetThresholds}
          />
          {isAlertOnly && (
            <>
              {showReportAlertSelectMenu && (
                <ReportAlertSelectMenu
                  selectedReports={selectedReports}
                  setSelectedReports={setSelectedReports}
                  allReports={allReports}
                />
              )}
              <AlertThresholds
                allowCustom={allowCustomThresholds}
                comparisonConfig={comparisonConfig}
                compareOps={allThresholdCompareOps}
                thresholds={alertThresholds}
                setThresholds={setAlertThresholds}
              />
            </>
          )}
        </>
      )}
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={handleClose}
          trackContext="schedule"
          trackAction="cancel"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={onConfirm}
          loading={isCreateMutationLoading || isUpdateScheduleLoading}
          disabled={isScheduleConfigLoading || !isConfirmButtonEnabled}
          trackContext="schedule"
          trackAction={currentSchedule ? 'update' : 'create'}
        >
          {currentSchedule ? 'Update' : 'Create'}
        </PrimaryButton>
      </ModalActions>
    </KubitModal>
  );
}
